import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { API_BASE } from '../config';
import {
  Activity,
  Target,
  TrendingUp,
  TrendingDown,
  Building2,
  ChevronRight,
  Zap,
  FileText,
  MessageSquare,
  ArrowRight,
  RefreshCw,
  BarChart3,
  Minus,
} from 'lucide-react';

// ---- Types ----

interface SearchHealth {
  overall_score: number;
  profile_completeness: number;
  market_alignment: number;
  application_activity: number;
  skill_coverage: number;
  top_suggestion: string;
  suggestion_link: string;
}

interface Funnel {
  discovered: number;
  saved: number;
  applied: number;
  interviewing: number;
  offered: number;
  rejected: number;
  conversion_rates: {
    save_rate: number;
    apply_rate: number;
    interview_rate: number;
    offer_rate: number;
  };
  insights: string[];
}

interface SkillMatrix {
  power_skills: Array<{ skill: string; proficiency: string; demand: number }>;
  invest_skills: Array<{ skill: string; demand: number; gap_severity: number }>;
  differentiators: Array<{ skill: string; proficiency: string; rarity: number }>;
  deprioritize: Array<{ skill: string; demand: number }>;
  total_demand_skills_missing: number;
  total_trending_user_has: number;
}

interface CompanyIntel {
  id: number;
  name: string;
  open_roles: number;
  avg_match: number;
  best_fit_score: number;
  best_fit_role: string | null;
  hiring_trend: 'hot' | 'growing' | 'flat' | 'declining';
  hiring_delta: number;
  industry: string;
  fortune_500_rank: number | null;
}

interface Trends {
  jobs_by_week: Array<{ week: string; total: number; executive: number; remote: number }>;
  trending_skills: Array<{ skill: string; demand_count: number; user_has: boolean }>;
  key_indicators: {
    new_jobs_this_week: number;
    vs_last_week_pct: number;
    remote_pct: number;
    total_open: number;
  };
}

interface ActionItem {
  priority: 'high' | 'medium' | 'low';
  icon: string;
  title: string;
  description: string;
  link: string;
  impact_estimate: string;
}

interface AnalyticsData {
  search_health: SearchHealth;
  funnel: Funnel;
  skill_matrix: SkillMatrix;
  companies: CompanyIntel[];
  trends: Trends;
  actions: ActionItem[];
  period_days: number;
}

// ---- Helpers ----

function scoreColor(score: number): string {
  if (score >= 75) return 'text-green-700 dark:text-green-400';
  if (score >= 50) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

function scoreBg(score: number): string {
  if (score >= 75) return 'bg-green-500';
  if (score >= 50) return 'bg-amber-500';
  return 'bg-red-500';
}

function scoreRingColor(score: number): string {
  if (score >= 75) return '#22c55e';
  if (score >= 50) return '#f59e0b';
  return '#ef4444';
}

function trendIcon(trend: string) {
  if (trend === 'hot' || trend === 'growing') return <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400" />;
  if (trend === 'declining') return <TrendingDown className="w-4 h-4 text-red-500 dark:text-red-400" />;
  return <Minus className="w-4 h-4 text-gray-400 dark:text-slate-500" />;
}

function trendLabel(trend: string, delta: number): string {
  if (trend === 'hot') return `Hot (+${delta})`;
  if (trend === 'growing') return `+${delta}`;
  if (trend === 'declining') return `${delta}`;
  return 'Flat';
}

function trendLabelColor(trend: string): string {
  if (trend === 'hot') return 'text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/30';
  if (trend === 'growing') return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20';
  if (trend === 'declining') return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20';
  return 'text-gray-500 dark:text-slate-400 bg-gray-100 dark:bg-slate-800';
}

function actionIcon(icon: string) {
  switch (icon) {
    case 'target': return <Target className="w-5 h-5" />;
    case 'message-square': return <MessageSquare className="w-5 h-5" />;
    case 'trending-up': return <TrendingUp className="w-5 h-5" />;
    case 'file-text': return <FileText className="w-5 h-5" />;
    case 'file-plus': return <FileText className="w-5 h-5" />;
    case 'clipboard-list': return <BarChart3 className="w-5 h-5" />;
    default: return <Zap className="w-5 h-5" />;
  }
}

// ---- Circular Score Gauge ----

function ScoreGauge({ score, size = 120 }: { score: number; size?: number }) {
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = (score / 100) * circumference;
  const color = scoreRingColor(score);

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke="#e5e7eb" strokeWidth={strokeWidth}
        />
        <motion.circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke={color} strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: circumference - progress }}
          transition={{ duration: 1, ease: 'easeOut' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-3xl font-bold ${scoreColor(score)}`}>{score}</span>
        <span className="text-xs text-gray-500 dark:text-slate-400">/ 100</span>
      </div>
    </div>
  );
}

// ---- Sub-Score Bar ----

function SubScoreBar({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-gray-600 dark:text-slate-400 w-40 shrink-0">{label}</span>
      <div className="flex-1 bg-gray-200 dark:bg-slate-700 rounded-full h-2.5">
        <motion.div
          className={`h-2.5 rounded-full ${scoreBg(value)}`}
          initial={{ width: 0 }}
          animate={{ width: `${value}%` }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        />
      </div>
      <span className={`text-sm font-semibold w-10 text-right ${scoreColor(value)}`}>{value}%</span>
    </div>
  );
}

// ---- Funnel Stage ----

function FunnelBar({ label, count, maxCount }: { label: string; count: number; maxCount: number }) {
  const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
  return (
    <div className="flex flex-col items-center gap-1 flex-1">
      <span className="text-xs text-gray-500 dark:text-slate-400">{label}</span>
      <span className="text-lg font-bold text-gray-900 dark:text-white">{count}</span>
      <div className="w-full bg-gray-100 dark:bg-slate-700 rounded h-3">
        <motion.div
          className="bg-primary-500 h-3 rounded"
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.6 }}
        />
      </div>
    </div>
  );
}

// ---- Main Component ----

const Analytics: React.FC = () => {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);

  const fetchData = (days: number) => {
    setLoading(true);
    fetch(`${API_BASE}/api/analytics/dashboard?period=${days}`, { credentials: 'include' })
      .then((r) => { if (!r.ok) throw new Error('Failed'); return r.json(); })
      .then((d) => setData(d as AnalyticsData))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData(period);
  }, [period]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Analytics</h1>
            <p className="text-gray-600 dark:text-slate-400 mt-1">Loading your search intelligence...</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 p-6 animate-pulse">
              <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-1/3 mb-4" />
              <div className="h-8 bg-gray-200 dark:bg-slate-700 rounded w-1/2 mb-2" />
              <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-2/3" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-20">
        <Activity className="w-12 h-12 text-gray-400 dark:text-slate-500 mx-auto mb-4" />
        <h2 className="text-xl font-medium text-gray-700 dark:text-slate-300">No analytics data available</h2>
        <p className="text-gray-500 dark:text-slate-400 mt-2">Upload a resume and scrape some jobs to get started.</p>
        <Link to="/resumes" className="inline-flex items-center mt-4 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700">
          <FileText className="w-4 h-4 mr-2" /> Upload Resume
        </Link>
      </div>
    );
  }

  const { search_health, funnel, skill_matrix, companies, trends, actions } = data;

  const funnelMax = Math.max(funnel.discovered, funnel.saved, funnel.applied, funnel.interviewing, funnel.offered, 1);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Analytics</h1>
          <p className="text-gray-600 dark:text-slate-400 mt-1">Your strategic job search command center</p>
        </div>
        <div className="flex items-center gap-2">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                period === d ? 'bg-primary-600 text-white border-primary-600' : 'bg-white dark:bg-navy-800 text-gray-600 dark:text-slate-400 border-gray-300 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800'
              }`}
              onClick={() => setPeriod(d)}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Section 1: Search Health */}
      <motion.div
        className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 p-6"
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      >
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Search Health</h2>
        <div className="flex flex-col lg:flex-row items-center gap-8">
          <ScoreGauge score={search_health.overall_score} size={140} />
          <div className="flex-1 space-y-3 w-full">
            <SubScoreBar label="Profile Completeness" value={search_health.profile_completeness} />
            <SubScoreBar label="Market Alignment" value={search_health.market_alignment} />
            <SubScoreBar label="Application Activity" value={search_health.application_activity} />
            <SubScoreBar label="Skill Coverage" value={search_health.skill_coverage} />
          </div>
        </div>
        <div className="mt-6 p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-100 dark:border-primary-800 rounded-xl flex items-start gap-3">
          <Zap className="w-5 h-5 text-primary-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-primary-800 dark:text-primary-300">{search_health.top_suggestion}</p>
          </div>
          <Link to={search_health.suggestion_link} className="text-sm font-medium text-primary-700 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 flex items-center shrink-0">
            Go <ChevronRight className="w-4 h-4 ml-0.5" />
          </Link>
        </div>
      </motion.div>

      {/* Section 2: Application Funnel */}
      <motion.div
        className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 p-6"
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
      >
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Application Pipeline</h2>
        <div className="flex items-end gap-2">
          <FunnelBar label="Discovered" count={funnel.discovered} maxCount={funnelMax} />
          <ArrowRight className="w-4 h-4 text-gray-300 dark:text-slate-600 mb-5 shrink-0" />
          <FunnelBar label="Saved" count={funnel.saved} maxCount={funnelMax} />
          <ArrowRight className="w-4 h-4 text-gray-300 dark:text-slate-600 mb-5 shrink-0" />
          <FunnelBar label="Applied" count={funnel.applied} maxCount={funnelMax} />
          <ArrowRight className="w-4 h-4 text-gray-300 dark:text-slate-600 mb-5 shrink-0" />
          <FunnelBar label="Interview" count={funnel.interviewing} maxCount={funnelMax} />
          <ArrowRight className="w-4 h-4 text-gray-300 dark:text-slate-600 mb-5 shrink-0" />
          <FunnelBar label="Offered" count={funnel.offered} maxCount={funnelMax} />
        </div>
        {/* Conversion rates */}
        <div className="mt-4 flex gap-4 text-xs text-gray-500 dark:text-slate-400 justify-center">
          <span>Save: {funnel.conversion_rates.save_rate}%</span>
          <span>Apply: {funnel.conversion_rates.apply_rate}%</span>
          <span>Interview: {funnel.conversion_rates.interview_rate}%</span>
          <span>Offer: {funnel.conversion_rates.offer_rate}%</span>
        </div>
        {funnel.insights.length > 0 && (
          <div className="mt-4 space-y-1">
            {funnel.insights.map((insight, i) => (
              <p key={i} className="text-sm text-gray-600 dark:text-slate-400 flex items-start gap-2">
                <span className="text-primary-500 mt-0.5">•</span> {insight}
              </p>
            ))}
          </div>
        )}
      </motion.div>

      {/* Section 3 & 4: Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Section 3: Skill-Market Fit */}
        <motion.div
          className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 p-6"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
        >
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Skill-Market Fit</h2>
          <div className="grid grid-cols-2 gap-4">
            {/* Power Skills (top-right quadrant) */}
            <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
              <h3 className="text-xs font-semibold text-green-800 dark:text-green-400 uppercase mb-2">Power Skills</h3>
              <p className="text-[10px] text-green-600 dark:text-green-500 mb-2">High demand + High proficiency</p>
              {skill_matrix.power_skills.length > 0 ? (
                <div className="space-y-1">
                  {skill_matrix.power_skills.slice(0, 5).map((s) => (
                    <div key={s.skill} className="flex items-center justify-between text-xs">
                      <span className="text-green-900 dark:text-green-300 font-medium truncate">{s.skill}</span>
                      <span className="text-green-600 dark:text-green-500 shrink-0 ml-1">{s.demand}%</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-green-600 dark:text-green-500 italic">Complete your profile to see power skills</p>
              )}
            </div>

            {/* Invest (top-left quadrant) */}
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
              <h3 className="text-xs font-semibold text-amber-800 dark:text-amber-400 uppercase mb-2">Invest Here</h3>
              <p className="text-[10px] text-amber-600 dark:text-amber-500 mb-2">High demand + Skill gap</p>
              {skill_matrix.invest_skills.length > 0 ? (
                <div className="space-y-1">
                  {skill_matrix.invest_skills.slice(0, 5).map((s) => (
                    <div key={s.skill} className="flex items-center justify-between text-xs">
                      <span className="text-amber-900 dark:text-amber-300 font-medium truncate">{s.skill}</span>
                      <span className="text-amber-600 dark:text-amber-500 shrink-0 ml-1">{s.demand} jobs</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-amber-600 dark:text-amber-500 italic">No critical gaps detected</p>
              )}
            </div>

            {/* Differentiators (bottom-right) */}
            <div className="p-3 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-xl">
              <h3 className="text-xs font-semibold text-primary-800 dark:text-primary-400 uppercase mb-2">Differentiators</h3>
              <p className="text-[10px] text-primary-600 dark:text-primary-500 mb-2">Niche + High proficiency</p>
              {skill_matrix.differentiators.length > 0 ? (
                <div className="space-y-1">
                  {skill_matrix.differentiators.slice(0, 5).map((s) => (
                    <div key={s.skill} className="flex items-center justify-between text-xs">
                      <span className="text-primary-900 dark:text-primary-300 font-medium truncate">{s.skill}</span>
                      <span className="text-primary-600 dark:text-primary-500 shrink-0 ml-1">{s.proficiency}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-primary-600 dark:text-primary-500 italic">No rare skills detected yet</p>
              )}
            </div>

            {/* Deprioritize (bottom-left) */}
            <div className="p-3 bg-gray-50 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-800 rounded-xl">
              <h3 className="text-xs font-semibold text-gray-600 dark:text-slate-400 uppercase mb-2">Deprioritize</h3>
              <p className="text-[10px] text-gray-500 dark:text-slate-400 mb-2">Low demand + Low proficiency</p>
              {skill_matrix.deprioritize.length > 0 ? (
                <div className="space-y-1">
                  {skill_matrix.deprioritize.slice(0, 5).map((s) => (
                    <div key={s.skill} className="flex items-center justify-between text-xs">
                      <span className="text-gray-600 dark:text-slate-400 truncate">{s.skill}</span>
                      <span className="text-gray-400 dark:text-slate-500 shrink-0 ml-1">{s.demand}%</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500 dark:text-slate-400 italic">--</p>
              )}
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between text-xs text-gray-500 dark:text-slate-400">
            <span>Skills in demand you're missing: <strong className="text-amber-700 dark:text-amber-400">{skill_matrix.total_demand_skills_missing}</strong></span>
            <span>Your trending skills: <strong className="text-green-700 dark:text-green-400">{skill_matrix.total_trending_user_has}</strong></span>
          </div>
        </motion.div>

        {/* Section 4: Company Intelligence */}
        <motion.div
          className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 p-6"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
        >
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Company Intelligence</h2>
          {companies.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 dark:text-slate-400 border-b border-gray-200 dark:border-slate-800">
                    <th className="pb-2 font-medium">Company</th>
                    <th className="pb-2 font-medium text-center">Open</th>
                    <th className="pb-2 font-medium text-center">Fit</th>
                    <th className="pb-2 font-medium">Best Role</th>
                    <th className="pb-2 font-medium text-center">Trend</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                  {companies.slice(0, 10).map((co) => (
                    <tr key={co.id} className="hover:bg-gray-50 dark:hover:bg-slate-800">
                      <td className="py-2">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-gray-400 dark:text-slate-500 shrink-0" />
                          <div>
                            <span className="font-medium text-gray-900 dark:text-white">{co.name}</span>
                            <p className="text-[10px] text-gray-400 dark:text-slate-500">{co.industry}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-2 text-center text-gray-700 dark:text-slate-300">{co.open_roles}</td>
                      <td className="py-2 text-center">
                        <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${
                          co.best_fit_score >= 75 ? 'text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/30' :
                          co.best_fit_score >= 50 ? 'text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30' :
                          'text-gray-600 dark:text-slate-400 bg-gray-100 dark:bg-slate-800'
                        }`}>
                          {co.best_fit_score > 0 ? `${co.best_fit_score}%` : '--'}
                        </span>
                      </td>
                      <td className="py-2 text-xs text-gray-600 dark:text-slate-400 max-w-[140px] truncate">{co.best_fit_role || '--'}</td>
                      <td className="py-2 text-center">
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded ${trendLabelColor(co.hiring_trend)}`}>
                          {trendIcon(co.hiring_trend)}
                          {trendLabel(co.hiring_trend, co.hiring_delta)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <Building2 className="w-8 h-8 text-gray-300 dark:text-slate-600 mx-auto mb-2" />
              <p className="text-sm text-gray-500 dark:text-slate-400">No companies with open roles yet. Start scraping!</p>
            </div>
          )}
          <div className="mt-3 text-right">
            <Link to="/companies" className="text-sm text-primary-600 hover:text-primary-800">
              View all companies →
            </Link>
          </div>
        </motion.div>
      </div>

      {/* Section 5: Market Trends */}
      <motion.div
        className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 p-6"
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
      >
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Market Trends</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Key Indicators */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-3">Key Indicators</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-gray-50 dark:bg-slate-800/50 rounded-xl">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{trends.key_indicators.new_jobs_this_week}</p>
                <p className="text-xs text-gray-500 dark:text-slate-400">New jobs this week</p>
                {trends.key_indicators.vs_last_week_pct !== 0 && (
                  <p className={`text-xs mt-1 ${trends.key_indicators.vs_last_week_pct > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                    {trends.key_indicators.vs_last_week_pct > 0 ? '+' : ''}{trends.key_indicators.vs_last_week_pct}% vs last week
                  </p>
                )}
              </div>
              <div className="p-3 bg-gray-50 dark:bg-slate-800/50 rounded-xl">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{trends.key_indicators.total_open}</p>
                <p className="text-xs text-gray-500 dark:text-slate-400">Total open jobs</p>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-slate-800/50 rounded-xl">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{trends.key_indicators.remote_pct}%</p>
                <p className="text-xs text-gray-500 dark:text-slate-400">Remote roles</p>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-slate-800/50 rounded-xl">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{companies.length}</p>
                <p className="text-xs text-gray-500 dark:text-slate-400">Companies hiring</p>
              </div>
            </div>

            {/* Mini bar chart - Jobs by Week */}
            {trends.jobs_by_week.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Jobs by Week</h3>
                <div className="flex items-end gap-1 h-24">
                  {trends.jobs_by_week.map((w, i) => {
                    const maxJobs = Math.max(...trends.jobs_by_week.map((x) => x.total), 1);
                    const height = (w.total / maxJobs) * 100;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <motion.div
                          className="w-full bg-primary-500 rounded-t"
                          initial={{ height: 0 }}
                          animate={{ height: `${height}%` }}
                          transition={{ duration: 0.5, delay: i * 0.05 }}
                          title={`Week ${w.week}: ${w.total} jobs`}
                        />
                        <span className="text-[8px] text-gray-400 dark:text-slate-500">{w.total}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Trending Skills */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-3">Trending Skills</h3>
            {trends.trending_skills.length > 0 ? (
              <div className="space-y-2">
                {trends.trending_skills.map((s, i) => {
                  const maxDemand = trends.trending_skills[0]?.demand_count || 1;
                  const pct = (s.demand_count / maxDemand) * 100;
                  return (
                    <div key={s.skill} className="flex items-center gap-3">
                      <span className="text-xs text-gray-400 dark:text-slate-500 w-4">{i + 1}</span>
                      <span className={`text-sm w-32 truncate ${s.user_has ? 'text-green-700 dark:text-green-400 font-medium' : 'text-gray-700 dark:text-slate-300'}`}>
                        {s.user_has && '✓ '}{s.skill}
                      </span>
                      <div className="flex-1 bg-gray-100 dark:bg-slate-700 rounded-full h-2">
                        <motion.div
                          className={`h-2 rounded-full ${s.user_has ? 'bg-green-500' : 'bg-primary-400'}`}
                          initial={{ width: 0 }}
                          animate={{ width: `${pct}%` }}
                          transition={{ duration: 0.5, delay: i * 0.03 }}
                        />
                      </div>
                      <span className="text-xs text-gray-500 dark:text-slate-400 w-12 text-right">{s.demand_count} jobs</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-slate-400">No skill data yet. Scrape jobs first.</p>
            )}
            <div className="mt-3">
              <Link to="/intelligence" className="text-sm text-primary-600 hover:text-primary-800">
                Full skill analysis →
              </Link>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Section 6: Action Items */}
      <motion.div
        className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 p-6"
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
      >
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recommended Actions</h2>
        {actions.length > 0 ? (
          <div className="space-y-3">
            {actions.map((action, i) => (
              <Link key={i} to={action.link}>
                <div className={`flex items-start gap-4 p-4 rounded-xl border transition-colors hover:bg-gray-50 dark:hover:bg-slate-800 ${
                  action.priority === 'high' ? 'border-primary-200 dark:border-primary-800 bg-primary-50/30 dark:bg-primary-900/20' : 'border-gray-200 dark:border-slate-800'
                }`}>
                  <div className={`p-2 rounded-xl shrink-0 ${
                    action.priority === 'high' ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-400' : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-400'
                  }`}>
                    {actionIcon(action.icon)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{action.title}</p>
                    <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">{action.description}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                      action.priority === 'high' ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-400' : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-400'
                    }`}>
                      {action.impact_estimate}
                    </span>
                    <ChevronRight className="w-4 h-4 text-gray-400 dark:text-slate-500" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-6">
            <RefreshCw className="w-8 h-8 text-gray-300 dark:text-slate-600 mx-auto mb-2" />
            <p className="text-sm text-gray-500 dark:text-slate-400">No actions right now. Your search is on track!</p>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default Analytics;
